import { Box, Typography, Chip } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { ImageField, useTranslate } from 'react-admin';
import {
    summaryStepChipStyles,
    summaryStepContainerStyles,
    summaryStepTypographyStyles,
    summaryStepImageStyles,
    summaryStepLayoutStyles,
} from '@/themes/commonStyles';
import { serviceRequestPriorityChoices } from '@/components/global/options/selectOptions';
import { NoImage } from '@/components/global/fields/NoImage';
import { couponValidityChoices } from '@/constants/CampaignSteps';

const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const getPriorityChipProps = (priority, translate) => {
    const priorityChoice = serviceRequestPriorityChoices(translate).find(choice => choice.id === priority);
    if (!priorityChoice) return { label: priority, backgroundColor: '#F2F2F2' };

    return {
        label: `${priorityChoice.name} ${translate('resources.campaign.labels.priority')}`,
        backgroundColor: priorityChoice.color,
    };
};

const SummaryStepEmissionInfo = () => {
    const translate = useTranslate();
    const startDate = useWatch({ name: 'startDate' });
    const endDate = useWatch({ name: 'endDate' });
    const priority = useWatch({ name: 'priority' });
    const sponsored = useWatch({ name: 'sponsored' });
    const couponImage = useWatch({ name: 'image' });
    const deviceDescription = useWatch({ name: 'deviceDescription' });
    const couponValidity = useWatch({ name: 'imageDisplayDays' });

    const durationText = startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : translate('resources.campaign.labels.noDates');

    const priorityProps = getPriorityChipProps(priority, translate);

    const validityText = couponValidityChoices.find(choice => choice.id === couponValidity)?.name || `7 ${translate('analytics.common.days')}`;

    const getImageUrl = () => {
        if (!couponImage) return null;
        const file = Array.isArray(couponImage) ? couponImage[0] : couponImage;
        if (!file) return null;
        if (typeof file === 'string') return file;
        if (file.fileUrl) return file.fileUrl;
        const rawFile = file.rawFile || file;
        if (rawFile instanceof File) {
            return URL.createObjectURL(rawFile);
        }
        return file.src || file;
    };

    const imageUrl = getImageUrl();

    return (
        <Box sx={summaryStepLayoutStyles.flexColumn}>
            <Chip label={durationText} sx={{ ...summaryStepChipStyles.primary, height: '36px', width: 'max-content' }} />
            <Box sx={summaryStepLayoutStyles.flexRow}>
                <Chip
                    label={priorityProps.label}
                    sx={{
                        ...summaryStepChipStyles.small,
                        backgroundColor: priorityProps.backgroundColor,
                    }}
                />
                {sponsored && <Chip label={translate('resources.campaign.labels.sponsored')} sx={summaryStepChipStyles.sponsored} />}
            </Box>

            {(imageUrl || deviceDescription) && (
                <Box sx={summaryStepContainerStyles.productCard}>
                    <Box sx={summaryStepContainerStyles.imageContainerLarge}>
                        {imageUrl ? (
                            <ImageField
                                source='src'
                                record={{ src: imageUrl }}
                                title={translate('resources.campaign.labels.couponImageTitle')}
                                sx={summaryStepImageStyles.couponImage}
                            />
                        ) : (
                            <NoImage sx={summaryStepImageStyles.noImage} />
                        )}
                    </Box>
                    <Box sx={summaryStepContainerStyles.contentContainer}>
                        <Typography variant='body2' sx={summaryStepTypographyStyles.description}>
                            {deviceDescription}
                        </Typography>
                    </Box>
                </Box>
            )}
            <Chip
                label={
                    <Box sx={{ display: 'inline-flex', gap: '4px' }}>
                        {translate('resources.campaign.labels.couponValidityLabel')} <Box sx={{ fontWeight: 700 }}>{validityText}</Box>
                    </Box>
                }
                sx={{ ...summaryStepChipStyles.primary, height: '36px', width: 'max-content' }}
            />
        </Box>
    );
};

export default SummaryStepEmissionInfo;
